/**
 * ToolCallGroup wraps consecutive tool-call parts in a collapsible
 * disclosure, matching V1's CollapsedToolCalls pattern.
 *
 * Used as the `ToolGroup` component in MessagePrimitive.Parts.
 * Assistant-UI automatically groups consecutive tool-call parts and
 * passes them through this wrapper.
 *
 * Single tool calls pass through without the collapsible wrapper.
 */
import { useEffect, useState, type FC, type PropsWithChildren } from "react";
import { useAuiState } from "@assistant-ui/react";
import { ChevronRight } from "lucide-react";
import { cn } from "~/lib/utils";

export const ToolCallGroup: FC<PropsWithChildren<{ startIndex: number; endIndex: number }>> = ({
  children,
  startIndex,
  endIndex,
}) => {
  const message = useAuiState((s) => s.message);
  const isRunning = useAuiState((s) => s.message.status?.type === "running");
  const count = endIndex - startIndex + 1;

  const [isExpanded, setIsExpanded] = useState(isRunning);

  // Auto-collapse when run finishes
  useEffect(() => {
    if (!isRunning) {
      setIsExpanded(false);
    }
  }, [isRunning]);

  // Hide the group entirely while all tools are still pending —
  // the reasoning shimmer text is shown instead.
  const allToolsPending = message.content
    ?.slice(startIndex, endIndex + 1)
    .every((p: { type: string; result?: unknown }) => p.type === "tool-call" && p.result == null);
  if (allToolsPending && isRunning) return null;

  if (count <= 1) {
    return <>{children}</>;
  }

  return (
    <div className="px-2 pt-2">
      <details
        open={isExpanded}
        onToggle={(e) => {
          const target = e.target as HTMLDetailsElement;
          setIsExpanded(target.open);
          e.stopPropagation();
        }}
      >
        <summary className="list-none cursor-pointer select-none w-fit">
          <div className="flex items-center">
            <span className="text-xs font-medium text-purple-600 px-1">{count} tool calls</span>
            <ChevronRight
              className={cn(
                "size-4 text-purple-600 transition-transform duration-200",
                isExpanded && "rotate-90"
              )}
            />
          </div>
        </summary>
        <div className="mt-3 space-y-2">{children}</div>
      </details>
    </div>
  );
};
